import React from 'react';
import { Car, DollarSign, Zap, Clock, Tag } from 'lucide-react';

/**
 * Компонент параметров автомобиля для расчета налога
 */
const CarParametersSection = ({ 
  formData, 
  handleInputChange, 
  errors,
  carTypes
}) => {
  return (
    <div className="mb-6 bg-blue-50 dark:bg-blue-900/20 p-5 rounded-lg border border-blue-200 dark:border-blue-800">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Car className="w-5 h-5 mr-2 text-blue-500" />
        Параметры автомобиля
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Стоимость автомобиля */}
        <div>
          <label htmlFor="carCost" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <DollarSign className="w-4 h-4 mr-2 text-blue-500" />
            Стоимость автомобиля (₽)
          </label>
          <input
            type="text"
            id="carCost"
            name="carCost"
            value={formData.carCost}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.carCost ? 'input-error' : ''}`}
            placeholder="Введите стоимость автомобиля"
          />
          {errors.carCost && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.carCost}
            </p>
          )}
        </div>

        {/* Мощность двигателя */}
        <div>
          <label htmlFor="carPower" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <Zap className="w-4 h-4 mr-2 text-blue-500" />
            Мощность двигателя (л.с.)
          </label>
          <input
            type="text"
            id="carPower"
            name="carPower"
            value={formData.carPower}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.carPower ? 'input-error' : ''}`}
            placeholder="Введите мощность двигателя"
          />
          {errors.carPower && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.carPower}
            </p>
          )}
        </div>

        {/* Возраст автомобиля */}
        <div>
          <label htmlFor="carAge" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <Clock className="w-4 h-4 mr-2 text-blue-500" />
            Возраст автомобиля (лет)
          </label>
          <input
            type="text"
            id="carAge"
            name="carAge"
            value={formData.carAge}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.carAge ? 'input-error' : ''}`}
            placeholder="Введите возраст автомобиля"
          />
          {errors.carAge && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.carAge}
            </p>
          )}
        </div>

        {/* Тип автомобиля */}
        <div>
          <label htmlFor="carType" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <Tag className="w-4 h-4 mr-2 text-blue-500" />
            Тип автомобиля
          </label>
          <select
            id="carType"
            name="carType"
            value={formData.carType}
            onChange={handleInputChange}
            className={`form-select w-full ${errors.carType ? 'input-error' : ''}`}
          >
            {carTypes.map((type) => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </select>
          {errors.carType && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.carType}
            </p>
          )}
        </div>

        {/* Дополнительные опции */}
        <div className="col-span-full">
          <div className="flex flex-col space-y-2">
            <label className="inline-flex items-center">
              <input
                type="checkbox"
                name="isElectric"
                checked={formData.isElectric}
                onChange={handleInputChange}
                className="form-checkbox"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Электромобиль (налоговая льгота)
              </span>
            </label>
            
            <label className="inline-flex items-center">
              <input
                type="checkbox"
                name="isFirstRegistration"
                checked={formData.isFirstRegistration}
                onChange={handleInputChange}
                className="form-checkbox"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Первичная регистрация в России
              </span>
            </label>
          </div>
        </div>
      </div>

      {/* Информация о повышающих коэффициентах */}
      {parseFloat(formData.carCost) >= 3000000 && (
        <div className="mt-4 p-3 bg-blue-100 dark:bg-blue-900/40 rounded-lg text-sm text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800">
          <p className="font-medium">Информация о повышающем коэффициенте:</p>
          <ul className="list-disc list-inside mt-1 space-y-1">
            <li>От 3 до 5 млн ₽: коэффициент 1.1 для автомобилей до 3 лет</li>
            <li>От 5 до 10 млн ₽: коэффициент 2 для автомобилей до 5 лет</li>
            <li>От 10 до 15 млн ₽: коэффициент 3 для автомобилей до 10 лет</li>
            <li>От 15 млн ₽: коэффициент 3 для автомобилей до 20 лет</li>
          </ul>
          {formData.carType === 'luxury' && (
            <p className="mt-2 font-medium">
              Автомобиль относится к списку роскошных ТС, опубликованному Минпромторгом России.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default CarParametersSection;